import { validateTokenStructure, TokenValidationError } from '../utils/tokenValidator';
import { serverLogger } from '../libs/datadog-logger/loggers/serverLogger';

/**
 * Validates tokens returned from authentication endpoints.
 * Throws an error when tokens are malformed so callers can reject the response.
 *
 * @param {import('axios').AxiosResponse} response
 * @param {{ debugValidation: import('debug').Debugger, debugError: import('debug').Debugger }} loggers
 */
export function validateAuthEndpointResponse(response, { debugValidation, debugError }) {
  const config = response?.config || {};
  const url = config.url || '';
  const isAuthEndpoint = url.includes('/auth/login') || url.includes('/auth/refresh');

  if (!isAuthEndpoint || !response?.data) return;

  debugValidation('step 6: auth endpoint response - validating tokens', {
    url,
    hasAccessToken: !!response.data.access_token,
    hasRefreshToken: !!response.data.refresh_token,
  });

  try {
    if (response.data.access_token) {
      debugValidation('step 6: validating access_token from API');
      validateTokenStructure(response.data.access_token);
      debugValidation('step 6: access_token validation passed');
    }

    if (response.data.refresh_token) {
      debugValidation('step 6: validating refresh_token from API');
      if (
        typeof response.data.refresh_token !== 'string' ||
        response.data.refresh_token.trim().length === 0
      ) {
        throw new TokenValidationError('Refresh token must be a non-empty string', 'refresh_token');
      }
      debugValidation('step 6: refresh_token validation passed (opaque string)');
    }

    console.debug('[httpClient] API tokens validated successfully');
  } catch (validationError) {
    debugError('step 6: API token validation failed', {
      url,
      errorMessage: validationError.message,
      field: validationError instanceof TokenValidationError ? validationError.field : 'unknown',
    });
    console.error('[httpClient] API returned invalid token:', validationError);

    debugValidation('step 6: tracking API validation failure in Datadog');
    serverLogger.error('auth.api_token_validation_failed', {
      source: 'api_token_validation',
      endpoint: url,
      type: validationError instanceof TokenValidationError ? validationError.field : 'unknown',
      errorMessage: validationError.message,
    });

    throw new Error(`Backend returned invalid token: ${validationError.message}`);
  }
}
